import { useEffect, useState } from 'react';

import { ReactComponent as LogoMini } from '../assets/LogoMini.svg';
import LOADER_MESSAGES from './loaderMessages.json';

import { cn } from '@/lib/utils';

declare global {
  interface Window {
    __bootSplashStart?: number;
  }
}

const messageAt = (elapsed: number) =>
  LOADER_MESSAGES.reduce(
    (current, { at, text }) => (at <= elapsed ? text : current),
    LOADER_MESSAGES[0].text,
  );

// Loaders that follow each other closely (splash → App → EntryRouter) count as
// one wait, timed from when the splash first appeared.
const HANDOVER_GAP_MS = 500;
const FADE_MS = 300;
let waitStart = window.__bootSplashStart ?? performance.now();
let lastShown = performance.now();

interface FullScreenLoaderProps {
  error?: string;
}

// Mirrors #boot-splash in index.html; the negative delay keeps every spinner on
// the same rotation phase so the splash → React handover doesn't jump.
export const FullScreenLoader = ({ error }: FullScreenLoaderProps) => {
  const [animationDelay] = useState(() => `-${performance.now()}ms`);
  const [start] = useState(() => {
    if (performance.now() - lastShown > HANDOVER_GAP_MS) {
      waitStart = performance.now();
    }
    return waitStart;
  });
  const [message, setMessage] = useState(() =>
    messageAt(performance.now() - start),
  );

  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const elapsed = performance.now() - start;
    const timers = LOADER_MESSAGES.filter(({ at }) => at > elapsed).flatMap(
      ({ at, text }) => [
        setTimeout(
          () => setVisible(false),
          start + at - FADE_MS - performance.now(),
        ),
        setTimeout(
          () => {
            setMessage(text);
            setVisible(true);
          },
          start + at - performance.now(),
        ),
      ],
    );
    return () => {
      timers.forEach(clearTimeout);
      lastShown = performance.now();
    };
  }, [start]);

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 px-6">
      <div className="relative grid size-16 place-items-center">
        {!error && (
          <div
            className="absolute inset-0 animate-spin rounded-full border-[3px] border-gray-200 border-t-indigo-900"
            style={{ animationDelay }}
          />
        )}
        <LogoMini className="h-auto w-[30px]" />
      </div>
      <p
        role="status"
        className={cn(
          'm-0 min-h-10 max-w-64 text-balance text-center font-sans text-sm leading-5 text-gray-500 transition-opacity duration-300 motion-reduce:transition-none',
          !error && !visible && 'opacity-0',
        )}
      >
        {error ?? message}
      </p>
    </div>
  );
};
